import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { BehaviorSubject, Observable } from 'rxjs';
import {
  BasicMetaData,
  CompanyMetaModel,
  InvoiceModelExpectBackend,
  Order,
  Sort,
  TemplateExpectBackend,
  TemplateModel,
  TYPE_SEARCH
} from 'shared';
import { PaginateQuery, PaginatorModel, UUID } from 'symbols';
import { InvoiceFilterModel, InvoiceModel } from '../symbols';

@Injectable()
export class InvoiceService {
  readonly apiUrl = '/api/invoices';
  public sortDefault$ = new BehaviorSubject({ sort: Sort.CREATED, order: Order.DESC });

  constructor(private http: HttpClient) {
  }

  getInvoiceList(filter: PaginateQuery<InvoiceFilterModel>): Observable<PaginatorModel<InvoiceModel>> {
    const params: { [key: string]: any } = {
      page: filter?.page || 1,
      limit: filter?.limit || 50,
      sortBy: `${filter?.sortBy[0][0]}:${filter?.sortBy[0][1]}`,
      typeSearch: filter?.filter?.typeSearch || TYPE_SEARCH.BY_METADATA
    };

    if (filter?.filter?.typeSearch === TYPE_SEARCH.BY_NUMBER) {
      params['filter.docNumber'] = filter?.filter?.searchText || '';
    } else {
      params['search'] = filter?.filter?.searchText || '';
    }

    return this.http.get<PaginatorModel<InvoiceModel>>(`${this.apiUrl}/invoice-list`, { params });
  }

  getInvoiceListLazy(url: string): Observable<PaginatorModel<InvoiceModel>> {
    return this.http.get<PaginatorModel<InvoiceModel>>(url);
  }

  getNextMetaData(): Observable<BasicMetaData> {
    return this.http.get<BasicMetaData>(`${this.apiUrl}/next-meta-data`);
  }

  getInvoiceById(id: string): Observable<InvoiceModel> {
    return this.http.get<InvoiceModel>(`${this.apiUrl}/invoice/${id}`);
  }

  createInvoice(date: InvoiceModelExpectBackend): Observable<InvoiceModel> {
    return this.http.post<InvoiceModel>(`${this.apiUrl}`, date);
  }

  createWaybill(invoiceId: string): Observable<InvoiceModel> {
    return this.http.post<InvoiceModel>(`${this.apiUrl}/create-waybill/${invoiceId}`, {});
  }

  createAcceptanceCertificate(invoiceId: string): Observable<InvoiceModel> {
    return this.http.post<InvoiceModel>(`${this.apiUrl}/create-acceptance-certificate/${invoiceId}`, {});
  }

  createServiceAgreement(invoiceId: string): Observable<InvoiceModel> {
    return this.http.post<InvoiceModel>(`${this.apiUrl}/create-service-agreement/${invoiceId}`, {});
  }

  createPurchaseAgreement(invoiceId: string): Observable<InvoiceModel> {
    return this.http.post<InvoiceModel>(`${this.apiUrl}/create-purchase-agreement/${invoiceId}`, {});
  }

  updateInvoice(id: UUID, date: InvoiceModelExpectBackend, saveAsForce: boolean): Observable<InvoiceModel> {
    return this.http.patch<InvoiceModel>(`${this.apiUrl}/${id}`, date);
  }

  getCompanyMetaByEgrpou(egrpou: string): Observable<CompanyMetaModel> {
    return this.http.get<CompanyMetaModel>(`${this.apiUrl}/company-meta`, { params: { egrpou } });
  }


  getInvoiceTemplateList(): Observable<TemplateModel[]> {
    return this.http.get<TemplateModel[]>(`${this.apiUrl}/invoice-template-list`);
  }

  updateInvoiceTemplate(id: string, template: TemplateExpectBackend, name: string): Observable<TemplateModel> {
    return this.http.patch<TemplateModel>(`${this.apiUrl}/invoice-template-list/${id}`, { ...template, name });
  }

  createInvoiceTemplate(name: string, template: TemplateExpectBackend): Observable<TemplateModel> {
    return this.http.post<TemplateModel>(`${this.apiUrl}/invoice-template-list/`, { ...template, name });
  }


  deleteInvoiceTemplate(id: string): Observable<void> {
    return this.http.delete<void>(`${this.apiUrl}/invoice-template-list/${id}`);
  }


  getWaybillTemplateList(): Observable<TemplateModel[]> {
    return this.http.get<TemplateModel[]>(`${this.apiUrl}/waybill-template-list`);
  }

  updateWaybillTemplate(id: string, template: TemplateExpectBackend, name: string): Observable<TemplateModel> {
    return this.http.patch<TemplateModel>(`${this.apiUrl}/waybill-template-list/${id}`, { ...template, name });
  }

  createWaybillTemplate(name: string, template: TemplateExpectBackend): Observable<TemplateModel> {
    return this.http.post<TemplateModel>(`${this.apiUrl}/waybill-template-list/`, { ...template, name });
  }


  deleteWaybillTemplate(id: string): Observable<void> {
    return this.http.delete<void>(`${this.apiUrl}/waybill-template-list/${id}`);
  }


  getServiceAgreementTemplateList(): Observable<TemplateModel[]> {
    return this.http.get<TemplateModel[]>(`${this.apiUrl}/service-agreement-template-list`);
  }

  updateServiceAgreementTemplate(id: string, template: TemplateExpectBackend, name: string): Observable<TemplateModel> {
    return this.http.patch<TemplateModel>(`${this.apiUrl}/service-agreement-template-list/${id}`, {
      ...template,
      name
    });
  }

  createServiceAgreementTemplate(name: string, template: TemplateExpectBackend): Observable<TemplateModel> {
    return this.http.post<TemplateModel>(`${this.apiUrl}/service-agreement-template-list/`, { ...template, name });
  }


  deleteServiceAgreementTemplate(id: string): Observable<void> {
    return this.http.delete<void>(`${this.apiUrl}/service-agreement-template-list/${id}`);
  }


  getAcceptanceCertificateTemplateList(): Observable<TemplateModel[]> {
    return this.http.get<TemplateModel[]>(`${this.apiUrl}/acceptance-certificate-template-list`);
  }

  updateAcceptanceCertificateTemplate(id: string, template: TemplateExpectBackend, name: string): Observable<TemplateModel> {
    return this.http.patch<TemplateModel>(`${this.apiUrl}/acceptance-certificate-template-list/${id}`, {
      ...template,
      name
    });
  }

  createAcceptanceCertificateTemplate(name: string, template: TemplateExpectBackend): Observable<TemplateModel> {
    return this.http.post<TemplateModel>(`${this.apiUrl}/acceptance-certificate-template-list/`, { ...template, name });
  }


  deleteAcceptanceCertificateTemplate(id: string): Observable<void> {
    return this.http.delete<void>(`${this.apiUrl}/acceptance-certificate-template-list/${id}`);
  }


  getPurchaseAgreementTemplateList(): Observable<TemplateModel[]> {
    return this.http.get<TemplateModel[]>(`${this.apiUrl}/purchase-agreement-template-list`);
  }

  updatePurchaseAgreementTemplate(id: string, template: TemplateExpectBackend, name: string): Observable<TemplateModel> {
    return this.http.patch<TemplateModel>(`${this.apiUrl}/purchase-agreement-template-list/${id}`, {
      ...template,
      name
    });
  }

  createPurchaseAgreementTemplate(name: string, template: TemplateExpectBackend): Observable<TemplateModel> {
    return this.http.post<TemplateModel>(`${this.apiUrl}/purchase-agreement-template-list/`, { ...template, name });
  }


  deletePurchaseAgreementTemplate(id: string): Observable<void> {
    return this.http.delete<void>(`${this.apiUrl}/purchase-agreement-template-list/${id}`);
  }

}
